import { Controller } from "@hotwired/stimulus";
import { Device } from "@twilio/voice-sdk";
import { post } from "@rails/request.js";
import { show, hide } from "../../../../frontend/javascript/utils";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { addBackGuard } from "../../../../frontend/javascript/back_guard";

// A dial the server refused. Unless the analyst can call anyway, dialing again would be refused too, so
// the Call button stays disabled. A refusal with no server message is a connection error worth retrying.
class DialRefused extends Error {
  constructor(message, overrideAllowed) {
    super(message || "Error Connecting");
    this.overrideAllowed = overrideAllowed;
    this.blocksDialing = Boolean(message) && !overrideAllowed;
  }
}

export default class extends Controller {
  static values = {
    phoneCallsUrl: String,
    phoneNumber: String,
    userId: String,
    orderId: String,
    callType: String,
    phoneId: String,
    twilioToken: String,
    callStatus: { type: String, default: "Not Connected" },
    isMuted: { type: Boolean, default: false },
  };

  static targets = [
    "callButton",
    "virtualPhone",
    "callStatusIndicator",
    "dtmfKey",
    "endCallButton",
    "muteButton",
    "phoneNumber",
    "refusal",
    "refusalMessage",
    "override",
    "overrideReason",
    "overrideConfirm",
    "keypad",
    "keypadToggle",
  ];

  connect() {
    // Leave null initially as chrome will block audio context until user interaction (leads to annoying console logs)
    this.device = null;
    this.call = null;
    this.phoneCallId = null;
    this.overrideReason = null;
    this.isOrderClosed = false;
    if (this.callTypeValue === "employer") this.#addSamePageGuard();
  }

  disconnect() {
    this.#removeLeaveGuards();
    this.removeSamePageGuard?.();
    this.device?.destroy();
  }

  callValueChanged() {
    if (this.call == null) {
      this.muteButtonTarget.disabled = true;
    }
  }

  callStatusValueChanged() {
    this.callStatusIndicatorTarget.innerText = this.callStatusValue;
  }

  isMutedValueChanged() {
    if (this.isMutedValue) {
      this.muteButtonTarget.innerText = "Unmute";
    } else {
      this.muteButtonTarget.innerText = "Mute";
    }
  }

  toggleDisabled(event) {
    this.callButtonTarget.disabled = !event.detail.phone;
  }

  updatePhoneNumber(event) {
    const phoneNumber = parsePhoneNumberFromString(event.detail.phone, "US");
    this.phoneNumberValue = phoneNumber?.number;
    this.phoneNumberTarget.innerText = phoneNumber?.formatNational() ?? "";
  }

  async startCall() {
    // Setup device on first call attempt or if destroyed
    if (!this.device || this.device.state === "destroyed") {
      this.device = this.#initializeDevice();
    }
    if (this.hasRefusalTarget) hide(this.refusalTarget);
    this.errored = false;
    this.callStatusValue = "Setting Up Call..."; // Set call status
    hide(this.callButtonTarget); // Hide call button
    show(this.virtualPhoneTarget); // Show dial pad

    try {
      // Request phone call object from backend and use to initiate call
      const phoneCall = await this.#requestPhoneCallObject(this.userIdValue, this.orderIdValue, this.phoneNumberValue);
      await this.#initiatePhoneCall(phoneCall.id);
    } catch (error) {
      console.error("[Twilio] Error initiating call:", error);
      this.callStatusValue = "Error Connecting";
      if (this.callTypeValue !== "employer") return;

      const refusal = error instanceof DialRefused ? error : new DialRefused();
      this.#showRefusal(refusal);
      this.call = null;
      this.#resetDialer();
      if (refusal.blocksDialing) this.callButtonTarget.disabled = true;
    }
  }

  endCall() {
    this.callStatusValue = this.errored ? "Error Connecting" : "Not Connected";
    const call = this.call;
    this.call = null; // Clear first so the disconnect event this triggers doesn't end the call twice

    // Only disconnect if call is still active
    if (call && call.status() !== "closed") {
      call.disconnect(); // End the call
    }

    this.#resetDialer();
  }

  callLogCleared() {
    if (!this.awaitingLog || this.isOrderClosed) return;

    this.awaitingLog = false;
    this.callButtonTarget.disabled = false;
  }

  orderClosed() {
    this.isOrderClosed = true;
    if (this.hasOverrideTarget) hide(this.overrideTarget);
    if (!this.call) this.callButtonTarget.disabled = true;
  }

  overrideReasonChanged() {
    this.overrideConfirmTarget.disabled = this.overrideReasonTarget.value.trim() === "";
  }

  confirmOverride() {
    this.overrideReason = this.overrideReasonTarget.value.trim();
    if (!this.overrideReason) return;

    this.startCall();
  }

  sendDTMFTone(event) {
    const tone = event.target.getAttribute("data-tone");
    this.call?.sendDigits(tone);
  }

  toggleMute() {
    this.isMutedValue = !this.isMutedValue;
    this.call.mute(this.isMutedValue);
  }

  #initializeDevice() {
    const device = new Device(this.twilioTokenValue, { enableRingingState: true });
    device.on("error", (twilioError, _call) => {
      this.callStatusValue = "Device Error";
    });

    return device;
  }

  async #requestPhoneCallObject(userId, orderId, phoneNumber) {
    const phoneCall = {
      user_id: userId,
      order_id: orderId,
      phone_number: phoneNumber,
      call_type: this.callTypeValue,
    };
    if (this.phoneIdValue) phoneCall.phone_id = this.phoneIdValue;
    if (this.overrideReason) phoneCall.override_reason = this.overrideReason;
    this.overrideReason = null;

    try {
      const response = await post(this.phoneCallsUrlValue, {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone_call: phoneCall }),
      });

      const body = await response.json;
      if (this.callTypeValue === "employer" && !response.ok) {
        throw new DialRefused(body.error, body.override_allowed);
      }

      const phoneCallIds = document.getElementById("phone_call_ids_str");
      if (body.id && phoneCallIds) {
        const currentValue = phoneCallIds.value ? JSON.parse(phoneCallIds.value) : [];
        currentValue.push(body.id);
        phoneCallIds.value = JSON.stringify(currentValue);
      }

      if (body.id) {
        this.phoneCallId = body.id;
        window.dispatchEvent(new CustomEvent("dialer:call-started", { detail: { phoneCallId: body.id } }));
      }
      return body;
    } catch (error) {
      console.error("Error requesting phone call object:", error);
      throw error;
    }
  }

  async #initiatePhoneCall(phoneCallId) {
    const params = {
      To: this.phoneNumberValue,
      phone_call_id: phoneCallId,
    };

    this.call = await this.device.connect({ params: params });
    this.toggleDisabledButtons(false);
    this.call.on("ringing", () => this.#addLeaveGuards());
    this.call.on("accept", () => {
      this.callStatusValue = "Connected";
      this.#addLeaveGuards();
    });
    const call = this.call;
    call.on("disconnect", () => {
      if (this.call === call) this.endCall();
    });
    call.on("error", (_error) => {
      this.errored = true;
      this.callStatusValue = "Error Connecting";
      // Twilio doesn't promise a disconnect after an error, so reset here once the call has ended.
      if (call.status() === "closed" && this.call === call) this.endCall();
    });
    this.call.on("cancel", () => {
      this.callStatusValue = "Connection Canceled";
    });
  }

  toggleKeypad() {
    this.keypadTarget.hidden = !this.keypadTarget.hidden;
    this.keypadToggleTarget.setAttribute("aria-expanded", String(!this.keypadTarget.hidden));
  }

  toggleDisabledButtons(isDisabled) {
    this.muteButtonTarget.disabled = isDisabled;
    this.dtmfKeyTargets.forEach((key) => {
      key.disabled = isDisabled;
    });
  }

  #resetDialer() {
    hide(this.virtualPhoneTarget); // Hide dial pad
    if (this.hasKeypadTarget) {
      this.keypadTarget.hidden = true;
      this.keypadToggleTarget.setAttribute("aria-expanded", "false");
    }
    show(this.callButtonTarget); // Show call button
    this.toggleDisabledButtons(true);
    this.#removeLeaveGuards({ popGuardEntry: !this.leaving });
    if (this.isOrderClosed) this.callButtonTarget.disabled = true;

    if (this.phoneCallId) {
      // A VoE dial leaves an event to log, and the server refuses the next dial until it's logged.
      if (this.callTypeValue === "employer") {
        this.awaitingLog = true;
        this.callButtonTarget.disabled = true;
      }
      this.phoneCallId = null;
      window.dispatchEvent(new CustomEvent("dialer:call-ended"));
    }
  }

  #showRefusal(error) {
    this.refusalMessageTarget.textContent = error.message;
    if (error.overrideAllowed && !this.isOrderClosed) {
      this.overrideReasonTarget.value = "";
      this.overrideConfirmTarget.disabled = true;
      show(this.overrideTarget);
    } else {
      hide(this.overrideTarget);
    }
    show(this.refusalTarget);
  }

  // Guards a live VoE call against leaving the page: closing the tab, a Turbo link, and the Back button. Back
  // pops a history entry pushed when the call went live; the back guard runs before Turbo's own popstate
  // handler and stops it, so Turbo never restores the page under a live call.
  #addLeaveGuards() {
    if (this.leaveGuards || this.callTypeValue !== "employer") return;

    const beforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const beforeVisit = (event) => {
      event.preventDefault();
      if (!window.confirm("A call is in progress. Leave and end it?")) return;

      this.leaving = true;
      this.endCall();
      window.Turbo.visit(event.detail.url, { action: "replace" });
    };
    const popState = () => {
      if (window.confirm("End the call and leave?")) {
        this.endCall();
        window.history.back();
      } else {
        window.history.pushState({ ...window.history.state, dialerGuard: true }, "");
      }
      return true;
    };

    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("turbo:before-visit", beforeVisit);
    const removeBackGuard = addBackGuard(popState);
    window.history.pushState({ ...window.history.state, dialerGuard: true }, "");
    this.leaveGuards = { beforeUnload, beforeVisit, removeBackGuard };
  }

  // Between calls, Back or Forward between two history entries for this same page (the guard entry and the page's
  // own) would make Turbo re-fetch the page and drop a half-typed log. The address doesn't change, so there is
  // nothing to navigate to: swallow it. Also covers the step back off the guard entry after a hang-up.
  #addSamePageGuard() {
    const pageUrl = window.location.href;
    this.removeSamePageGuard = addBackGuard(() => !this.leaveGuards && window.location.href === pageUrl);
  }

  // After a hang-up on the page, steps back off the guard entry so Back leaves the page next time.
  #removeLeaveGuards({ popGuardEntry = false } = {}) {
    if (!this.leaveGuards) return;

    const { beforeUnload, beforeVisit, removeBackGuard } = this.leaveGuards;
    window.removeEventListener("beforeunload", beforeUnload);
    document.removeEventListener("turbo:before-visit", beforeVisit);
    removeBackGuard();
    this.leaveGuards = null;

    if (popGuardEntry && window.history.state?.dialerGuard) window.history.back();
  }
}
